import type { MergeableReportItem, MergeableReportsResponse, PaginationMetadata } from "~/types/global";
import { MergeableReportModel } from "root/domain/entities/list.merge.report.model";

export class MergeableReportsOutputDTO {
  private readonly items: MergeableReportItem[];
  private readonly reportModels: MergeableReportModel[];
  private readonly paginationData: PaginationMetadata;

  constructor(data: MergeableReportsResponse) {
    this.items = this.validateAndTransform(data.data);
    this.paginationData = data.pagination;
    this.reportModels = this.items.map(item => new MergeableReportModel(item));
  }

  private validateAndTransform(data: unknown): MergeableReportItem[] {
    if (!Array.isArray(data)) {
      console.error("Invalid data format for mergeable reports list: expected an array");
      throw new Error("Erro ao buscar lista de relatórios para combinar.");
    }

    return data.map((item, index) => {
      if (this.isValidMergeableReportItem(item)) {
        return item;
      }
      console.error(`Invalid mergeable report data at index ${index}`, item);
      throw new Error("Erro ao buscar lista de relatórios para combinar.");
    });
  }

  private isValidMergeableReportItem(item: any): item is MergeableReportItem {
    const requiredFields = [
      'report_name',
      'report_type',
      'report_search_args',
      'modified_at',
      'user_reports_id'
    ] as const;

    // Check if all required fields exist
    if (!requiredFields.every(field => field in item)) {
      return false;
    }

    const isValidNameOrEncrypted = typeof item.report_name === "string" ||
      (typeof item.report_name === "object" && item.report_name !== null);

    const isValidArgsOrEncrypted = typeof item.report_search_args === "object" &&
      item.report_search_args !== null;

    if (!isValidNameOrEncrypted) return false;
    if (!isValidArgsOrEncrypted) return false;
    if (typeof item.report_type !== "string") return false;
    if (typeof item.modified_at !== "string") return false;
    if (typeof item.user_reports_id !== "string") return false;

    return true;
  }

  public getReports(): ReadonlyArray<MergeableReportItem> {
    return this.items;
  }

  public getReportModels(): ReadonlyArray<MergeableReportModel> {
    return this.reportModels;
  }

  public getPagination(): PaginationMetadata {
    return this.paginationData;
  }

  public count(): number {
    return this.items.length;
  }

  public getTotalItems(): number {
    return this.paginationData.total_items;
  }

  public getCurrentPage(): number {
    return this.paginationData.current_page;
  }

  public getTotalPages(): number {
    return this.paginationData.total_pages;
  }

  public hasNextPage(): boolean {
    return this.paginationData.has_next;
  }

  public hasPreviousPage(): boolean {
    return this.paginationData.has_previous;
  }

  public getPageSize(): number {
    return this.paginationData.page_size;
  }

  /** Filter reports that can be merged (have valid user_reports_id) */
  public getMergeableReports(): ReadonlyArray<MergeableReportModel> {
    return this.reportModels.filter(model => model.canBeMerged);
  }

  /** Get reports by type */
  public getReportsByType(reportType: string): ReadonlyArray<MergeableReportModel> {
    return this.reportModels.filter(model => model.reportType === reportType);
  }

  /** Search reports by name */
  public searchByName(searchTerm: string): ReadonlyArray<MergeableReportModel> {
    const lowerSearchTerm = searchTerm.toLowerCase();
    return this.reportModels.filter(model =>
      model.name.toLowerCase().includes(lowerSearchTerm)
    );
  }
}
